// What Manage subscription and its sidebar badge say (PRD 114 §4), worked out
// from the billing-accounts list and the person's own Subscription. Kept apart
// from the components so the wording and the ordering are tested on their own.

import {
  BillingAccount,
  BillingAccountBusiness,
  BillingAccounts,
  BillingDeepLinkTask,
  BillingReinstateWith,
  FirmBilledClient,
  parseAccountKey,
} from '../services/billingAccountsService';
import { NETWORK_ERROR_CODE, SubscriptionApiError, requestFailedMessage } from '../services/subscriptionService';
import { ARRANGEMENT_COPY } from '../components/firm/arrangementCopy';
import { Subscription } from '../types/auth';
import { portalErrorMessage } from './subscribePageModel';
import { localISODate } from '../utils/localDate';

export const MANAGE_SUBSCRIPTION_PATH = '/subscription';

/**
 * `/subscription`, optionally deep-linked to one account and a task (§4.5).
 * A well-formed key is written as it is (`group:<uuid>` reads better in an
 * email than `group%3A<uuid>`, and a colon is legal in a query); anything
 * else is encoded.
 */
export const manageSubscriptionPath = (key?: string | null, task?: BillingDeepLinkTask | null): string => {
  if (!key) return MANAGE_SUBSCRIPTION_PATH;
  const account = parseAccountKey(key) ? key : encodeURIComponent(key);
  return `${MANAGE_SUBSCRIPTION_PATH}?account=${account}${task ? `&task=${task}` : ''}`;
};

/** Where Greenback support answers (the website's terms and privacy pages name it). */
export const SUPPORT_EMAIL = 'support@greenbackapp.com';

const ATTENTION_LABELS: Record<string, string> = {
  payment_failed: 'Payment failed',
  paused: 'Subscription paused',
  card_needed: 'Card needed',
  confirm_payment: 'Confirm payment',
};

/** What an attention code is called. A code this web does not know still needs the person. */
export const attentionLabel = (code: string): string => ATTENTION_LABELS[code] ?? 'Needs attention';

export type AttentionTone = 'negative' | 'warning';

/** Red for money that did not arrive; amber for a state the person has to act on. */
export const attentionTone = (code: string): AttentionTone =>
  code === 'payment_failed' || code === 'confirm_payment' ? 'negative' : 'warning';

const KIND_ORDER: Record<BillingAccount['kind'], number> = { personal: 0, group: 1, firm: 2 };

/** Personal, then groups alphabetically, then the firm (§4.1). */
export const orderAccounts = (accounts: BillingAccount[]): BillingAccount[] =>
  [...accounts].sort((a, b) =>
    KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
    || (a.name ?? '').localeCompare(b.name ?? '', 'en', { sensitivity: 'base' })
    || a.key.localeCompare(b.key));

/** The accounts the person manages, in page order. The server sends no other kind; this checks anyway. */
export const managedAccounts = (data: BillingAccounts | null): BillingAccount[] =>
  data ? orderAccounts(data.accounts.filter(account => account.can_manage)) : [];

/** Managed accounts with something for the person to do, in page order. */
export const accountsNeedingAttention = (data: BillingAccounts | null): BillingAccount[] =>
  managedAccounts(data).filter(account => account.attention !== null);

/** What an account is called on the page. */
export const accountName = (account: BillingAccount): string => {
  if (account.kind === 'personal') return 'Your Greenback Pro';
  if (account.kind === 'firm') return account.name ?? 'Your practice';
  return account.name ?? account.businesses[0]?.name ?? 'Your new business';
};

/**
 * The member business a link into a group's books opens: one the viewer owns
 * when the list says which, so the billing card there is theirs to act on
 * (a bookkeeper's seat on another member shows its read-only card, with no
 * Subscribe); otherwise the first. Null for a group with no business yet.
 */
export const booksBusiness = (account: BillingAccount): BillingAccountBusiness | null =>
  account.businesses.find(business => business.owner) ?? account.businesses[0] ?? null;

/**
 * Whether the group has any member business: one named to the viewer, or one
 * only counted (`other_businesses_count`, members whose books they cannot
 * open). Without the count (an older api), the named ones alone.
 */
export const hasBusinesses = (account: BillingAccount): boolean =>
  account.businesses.length > 0 || (account.other_businesses_count ?? 0) > 0;

/**
 * A group's members in a line (PRD 114 §4.1): those the viewer owns first,
 * then the rest named to them, in the list's order; any others as a count,
 * "Harbour Holdings, Anchor Bookkeeping and 3 other businesses". A member
 * with no name is counted with the others. Null when there is nobody to name
 * or count.
 */
export const memberLine = (account: BillingAccount): string | null => {
  const ordered = [...account.businesses].sort((a, b) => Number(b.owner) - Number(a.owner));
  const names = ordered.map(business => business.name).filter((name): name is string => !!name);
  const others = (account.other_businesses_count ?? 0) + (account.businesses.length - names.length);
  if (others === 0) return names.length > 0 ? names.join(', ') : null;
  const businesses = others === 1 ? 'business' : 'businesses';
  return names.length > 0 ? `${names.join(', ')} and ${others} other ${businesses}` : `${others} ${businesses}`;
};

// ─── Sections (Dave's decision 7) ────────────────────────────────────────────

/** Groups the person pays for themselves, by card or by Greenback's invoice. */
export const PAID_GROUPS_HEADING = 'Businesses you pay for';
/** Complimentary groups. */
export const COMPED_GROUPS_HEADING = 'Your Businesses';
/**
 * Groups a practice pays for, seen by the client who owns them (a section of
 * the page); and, for a practice's owner, the clients their practice pays
 * for (a subheading inside that practice's own card, PRD 114 §4.1).
 */
export const FIRM_BILLED_HEADING = 'Firm Billed Clients';

export type GroupSection = 'paid' | 'comped' | 'firm_billed';

/**
 * The heading a group sits under: a group its practice pays for (the
 * `firm_paid` rail) under "Firm Billed Clients", a complimentary one under
 * "Your Businesses", and every other under "Businesses you pay for", as the
 * page always had them. That includes a firm-paid client's own card
 * subscription (the api lists it on the card rail: it is theirs, and they pay
 * it), a group whose practice handed its bill back, one with nothing paid
 * yet, and a rail this web does not know.
 */
export const groupSection = (account: BillingAccount): GroupSection => {
  if (account.rail === 'firm_paid') return 'firm_billed';
  if (account.rail === 'comp') return 'comped';
  return 'paid';
};

/**
 * What a practice's Firm Billed Clients says when it pays for no client
 * (`firm_billed_clients: []`). Null, an api that does not say, is not none
 * (nil is not zero): it shows nothing at all (PRD 114 §4.1).
 */
export const NO_FIRM_CLIENTS_LINE = "Your practice pays for no client's subscription.";

/**
 * How a practice pays for one client, in the words its own screens already
 * use (ARRANGEMENT_COPY, PRD 114 §4.1): one copy of who-pays wording.
 */
export const firmClientArrangement = (client: FirmBilledClient): string =>
  ARRANGEMENT_COPY[client.rebilled ? 'firm_billed_rebilled' : 'firm_billed'].label;

/**
 * Where "Reinstate your subscription today here" goes (Dave's decision 8):
 * the stopped group's own card on this page, with the task its row asks
 * (resume, or card), or none for a lapsed group.
 */
export const reinstatePath = (reinstate: BillingReinstateWith): string =>
  manageSubscriptionPath(reinstate.key, reinstate.task);

/**
 * The accessible name of "here" (PRD 114 §4.1): its visible word first
 * (label in name), then the group it reinstates, as that group's card is
 * named, so a screen reader's list of links never reads a bare "here".
 * `group` is the group's own row on the page, when it is there.
 */
export const reinstateLinkLabel = (reinstate: BillingReinstateWith, group: BillingAccount | null): string =>
  `here, reinstate ${reinstate.name ?? group?.name ?? group?.businesses[0]?.name ?? 'your Business subscription'}`;

/** The DOM id of an account's card, for deep links and the "Included with" link. */
export const accountCardId = (key: string): string => `account-${key.replace(/[^A-Za-z0-9-]/g, '-')}`;

/** Statuses in which a card on the list's own rail is still being charged. */
export const CHARGING_STATUSES: ReadonlyArray<BillingAccount['status']> = ['active', 'trialing', 'past_due'];

/** The rails on which the person pays for their own Pro: Stripe's card, or RevenueCat's store. */
const PAID_PERSONAL_RAILS: ReadonlyArray<BillingAccount['rail']> = ['card', 'store'];

/**
 * Whether the person pays for Pro themselves while a Business group of theirs
 * also includes it: paying twice, with their own subscription to cancel.
 * The list says so itself: by card through Stripe (`rail` card) or still by
 * RevenueCat (`rail` store), while it charges. A cancel already scheduled
 * charges nothing more; Pro that is only included has `rail` none.
 */
export const paysForProBesideBusiness = (account: BillingAccount | null | undefined): boolean =>
  !!account?.included_with
  && PAID_PERSONAL_RAILS.includes(account.rail)
  && CHARGING_STATUSES.includes(account.status);

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date the person reads. A date-only value (`2026-11-05`) is built from its
 * parts: `new Date("2026-11-05")` is UTC midnight, the 4th in every US zone.
 * An instant goes through Date. Null when unreadable.
 */
export const formatBillingDate = (
  value: string | null | undefined,
  style: 'long' | 'short' = 'long'
): string | null => {
  if (!value) return null;
  const parts = DATE_ONLY.exec(value);
  const date = parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: style, day: 'numeric', year: 'numeric' });
};

/** The zone Greenback's own invoices date in (the api's PlatformBilling::ZONE). */
const PLATFORM_ZONE = 'America/New_York';

/**
 * The last paid day of an invoiced group, as YYYY-MM-DD: the Eastern date of
 * the instant before `current_period_end` (the instant access ends). A
 * date-only value is already that day. Null for nothing, or nothing readable.
 */
export const paidThroughDay = (value: string | null | undefined): string | null => {
  if (!value) return null;
  if (DATE_ONLY.test(value)) return value;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : localISODate(new Date(at - 1), PLATFORM_ZONE);
};

export type PillVariant = 'positive' | 'negative' | 'warning' | 'info' | 'neutral';

const STATUS_PILLS: Record<NonNullable<BillingAccount['status']>, { label: string; variant: PillVariant }> = {
  active: { label: 'Active', variant: 'positive' },
  trialing: { label: 'Trial', variant: 'info' },
  past_due: { label: 'Payment failed', variant: 'negative' },
  paused: { label: 'Paused', variant: 'warning' },
  cancelling: { label: 'Ending', variant: 'warning' },
  payment_needed: { label: 'Payment needed', variant: 'negative' },
  lapsed: { label: 'Lapsed', variant: 'neutral' },
  needs_payment_method: { label: 'Card needed', variant: 'warning' },
  none: { label: 'Not subscribed', variant: 'neutral' },
};

/** The status pill, or null for a status this web does not know (a newer api). */
export const statusPill = (account: BillingAccount): { label: string; variant: PillVariant } | null =>
  account.status ? STATUS_PILLS[account.status] : null;

/** How the bill travels, in a line. No card details: the list reads the database only. */
export const railLine = (account: BillingAccount): string | null => {
  switch (account.rail) {
    case 'card':
      return 'Paid by card';
    case 'invoice':
      return 'Invoiced monthly by Greenback';
    case 'firm_paid':
      // Named by the api (PRD 114 §4.3); an api that does not name it yet, generically.
      return account.paid_by ? `Paid by ${account.paid_by.name}` : "Paid by your accountant's practice";
    case 'comp': {
      const until = formatBillingDate(account.free_until);
      return until ? `Complimentary until ${until}` : 'Complimentary';
    }
    default:
      return null;
  }
};

/**
 * When it renews or ends. Nothing for a firm-paid group: the client is shown
 * no amounts or dates of their practice's (PRD 112 §4.6.4), and the server
 * sends none; nor for a state with no date worth saying.
 *
 * An invoiced group's `current_period_end` is the date it is paid through,
 * not a renewal: Greenback sends the next invoice, nothing charges a card
 * (PRD 114 §4.3). The api sends it as the instant access ends, 00:00 Eastern
 * the day after the last paid day ("2026-11-01T04:00:00Z" for paid through
 * Oct 31), so it is read as the Eastern day before that instant
 * (paidThroughDay): read as a local day it was Nov 1 to anyone in Eastern
 * time, UTC or Europe, while the billing card said Oct 31.
 *
 * A pause makes the books read-only only when the list asks the person to
 * resume it (`attention.code` `paused`). A pause something else keeps
 * writable (a firm, a comp, their own Business) asks nothing, because
 * resuming it would pay for the books twice, so it never says read-only; it
 * says so, and that the way out is to cancel, where Manage billing (Stripe's
 * portal) is on offer: a paused subscription can still restart charging.
 * An unnamed signup group has no books, so its pause is said of Business.
 */
export const termLine = (account: BillingAccount): string | null => {
  if (account.rail === 'firm_paid') return null;
  if (account.rail === 'invoice') {
    const paid = formatBillingDate(paidThroughDay(account.current_period_end));
    return paid ? `Paid through ${paid}` : null;
  }
  const periodEnd = formatBillingDate(account.current_period_end);
  switch (account.status) {
    case 'cancelling': {
      const ends = formatBillingDate(account.cancel_at) ?? periodEnd;
      return ends ? `Ends ${ends}` : null;
    }
    case 'active':
    case 'trialing':
      return periodEnd ? `Renews ${periodEnd}` : null;
    case 'lapsed':
      return periodEnd ? `Ended ${periodEnd}` : null;
    case 'past_due':
      return practiceCountdown(account);
    case 'paused': {
      const hasBooks = hasBusinesses(account);
      if (account.attention?.code === 'paused') {
        return hasBooks
          ? 'Paused: the books are read-only until it resumes.'
          : "Paused: Greenback Business isn't available until it resumes.";
      }
      const cancel = account.actions.includes('stripe_portal') ? ' You can cancel it in Manage billing.' : '';
      return hasBooks
        ? `Paused. Your books stay open without it, so resuming it would pay for them twice.${cancel}`
        : `Paused. Something else already covers it, so resuming it would pay twice.${cancel}`;
    }
    case 'needs_payment_method':
      // The only way here (Firms::GraceExpiryService): a practice's grace ran
      // out and handed the bill back to the business.
      return 'Your practice no longer pays for this business.';
    default:
      return null;
  }
};

/**
 * A practice in grace (PRD 114 §4.1): how long before its billing moves to
 * its clients, counted as its banner counts it (FirmBillingBanner, from
 * Firms::BillingAlert#days_remaining). Nothing for any other row, or without
 * the count (no grace started, or an api that does not send it).
 *
 * Only the clients on the practice's card move (Firms::GraceExpiryService:
 * active, firm-paid, Stripe rail), and `seats` counts exactly those. With
 * none, or no count, only the practice's own subscription ends, so that is
 * all it says: the banner says the same with its "No client books depend on
 * this subscription right now."
 */
const practiceCountdown = (account: BillingAccount): string | null => {
  const days = account.kind === 'firm' ? account.days_remaining : null;
  if (days === null) return null;
  const dayCount = `${days} ${days === 1 ? 'day' : 'days'}`;
  if ((account.seats ?? 0) === 0) {
    return days === 0 ? "Your practice's subscription ends imminently." : `${dayCount} before your practice's subscription ends.`;
  }
  if (days === 0) return 'Billing moves to your clients imminently.';
  return `${dayCount} before billing moves to your clients.`;
};

/** Statuses in which a group has been paid for, and may still be. */
const PAID_GROUP_STATUSES: ReadonlyArray<BillingAccount['status']> = ['active', 'trialing', 'past_due', 'paused', 'cancelling'];

/** Whether a group's subscription was paid for (and is not over), whatever it holds. */
export const groupIsPaidFor = (account: BillingAccount): boolean => PAID_GROUP_STATUSES.includes(account.status);

/**
 * What an unnamed signup group (no business yet) is. The api lists every
 * group the person owns with no member, which includes a signup checkout
 * never finished (Businesses::SignupGroup makes the group first): only one
 * paid for is "paid for".
 */
export const unnamedGroupLine = (account: BillingAccount): string => {
  if (groupIsPaidFor(account)) return 'Paid for, with no business set up in it yet.';
  if (account.status === 'none') return "Checkout wasn't finished, so nothing is paid for yet.";
  return 'No business is set up in it yet.';
};

/** The rails on which an unnamed signup group counts as paid: the group's own Stripe row, or a comp. */
const FIRST_BUSINESS_RAILS: ReadonlyArray<BillingAccount['rail']> = ['card', 'comp'];
/** The statuses in which it does. A pause is not paid for the api, so naming would be refused. */
const FIRST_BUSINESS_STATUSES: ReadonlyArray<BillingAccount['status']> = ['active', 'trialing', 'past_due', 'cancelling'];

/**
 * Whether this is a Business group the person paid for but left before
 * naming (D14): /business/start names its first business. The api's own rule
 * (Businesses::SignupGroup.paid_for) is the group's live Stripe row or comp,
 * so a pause and an invoice are left out, and a firm never pays for a group
 * with nothing in it. Fails closed: a row the person does not manage, or a
 * group whose hidden members an older api does not count, is never offered.
 */
export const awaitsFirstBusiness = (account: BillingAccount): boolean =>
  account.kind === 'group'
  && account.can_manage
  && account.businesses.length === 0
  && account.other_businesses_count === 0
  && FIRST_BUSINESS_RAILS.includes(account.rail)
  && FIRST_BUSINESS_STATUSES.includes(account.status);

/** A message to support about an unnamed group naming cannot finish, with the group to find. */
export const unnamedGroupSupportHref = (account: BillingAccount): string =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Set up my Greenback Business')}`
  + `&body=${encodeURIComponent(`Signup group: ${account.id ?? account.key}`)}`;

/** The refusal's own sentence, or null when the api sent none (the parser's stand-in). */
const serverSentence = (error: SubscriptionApiError): string | null =>
  error.message && error.message !== requestFailedMessage(error.status) ? error.message : null;

/** What to say when a group's billing portal cannot be opened. */
export const groupPortalErrorMessage = (error: SubscriptionApiError): string => {
  switch (error.code) {
    // The server's sentence names the rule (and, for a firm, the practice
    // that pays); these say it in words should it ever send none.
    case 'invoice_billed':
      return serverSentence(error)
        ?? "This business is billed by invoice, not by card, so there's no card portal for it.";
    case 'firm_billed':
      return serverSentence(error)
        ?? "Your accountant's practice pays for this business's subscription, so there's no card subscription of yours to manage.";
    case 'no_subscription':
    case 'no_billing_account':
      return "There's no card subscription on file for this business yet, so there's nothing to manage there.";
    case 'not_found':
      return "That subscription isn't one you manage.";
    case NETWORK_ERROR_CODE:
      return portalErrorMessage(error);
    default:
      return error.status === 429
        ? 'Too many attempts. Wait a minute and try again.'
        : portalErrorMessage(error);
  }
};

// ─── The sidebar badge (§4.4) ────────────────────────────────────────────────

export interface BadgeSummary {
  /** The line under the plan name, or null. */
  subtitle: string | null;
  /** The dot, when something needs the person. */
  tone: AttentionTone | null;
  /** True when the subtitle is attention, which the tooltip and the phone header repeat. */
  needsAttention: boolean;
  /** Where the badge goes. Never with a task: clicking the menu item opens nothing by itself. */
  to: string;
}

const isFuture = (iso: string | undefined, now: Date): boolean => {
  if (!iso) return false;
  const date = new Date(iso);
  return !Number.isNaN(date.getTime()) && date > now;
};

/**
 * What needs attention, under a title that names the person's plan: their
 * own Pro's attention as it is ("Payment failed"); anyone else's account by
 * name ("Smith CPA: payment failed"), or a practice's failed card would read
 * as a failed Pro payment; two or more as a count.
 */
const attentionSubtitle = (attention: BillingAccount[]): string => {
  if (attention.length > 1) return `${attention.length} need attention`;
  const [only] = attention;
  const label = attentionLabel(only.attention!.code);
  return only.kind === 'personal' ? label : `${accountName(only)}: ${label.toLowerCase()}`;
};

/**
 * The badge's subtitle, dot and link, by the first rule that matches (§4.4):
 * something to fix; a personal cancel already scheduled; Pro that comes with
 * Business; free until a date; renewing; free. With no list (an api that does
 * not serve it yet) the rules that need it are skipped and the badge reads as
 * it always did, from the Subscription.
 *
 * `isBusiness`: the badge's title already says Greenback Business, so Pro
 * that comes with it reads "Pro included" rather than repeating Business.
 */
export const badgeSummary = ({
  subscription,
  isPro,
  isBusiness = false,
  data,
  now = new Date(),
}: {
  subscription: Subscription | undefined;
  isPro: boolean;
  isBusiness?: boolean;
  data: BillingAccounts | null;
  now?: Date;
}): BadgeSummary => {
  const attention = accountsNeedingAttention(data);
  if (attention.length > 0) {
    const codes = attention.map(account => account.attention!.code);
    return {
      subtitle: attentionSubtitle(attention),
      tone: codes.some(code => attentionTone(code) === 'negative') ? 'negative' : 'warning',
      needsAttention: true,
      to: manageSubscriptionPath(attention[0].key),
    };
  }

  const plain = (subtitle: string | null): BadgeSummary =>
    ({ subtitle, tone: null, needsAttention: false, to: MANAGE_SUBSCRIPTION_PATH });
  const personal = data?.accounts.find(account => account.kind === 'personal' && account.can_manage) ?? null;

  // A cancel already scheduled: it ends, it does not renew.
  if (personal?.status === 'cancelling') {
    const ends = formatBillingDate(personal.cancel_at ?? personal.current_period_end, 'short');
    if (ends) return plain(`Ends ${ends}`);
  }
  if (subscription?.status === 'cancelled' && isFuture(subscription.expires_at, now)) {
    return plain(`Ends ${formatBillingDate(subscription.expires_at, 'short')}`);
  }

  // Pro the person also pays for themselves renews like any other: saying it
  // is included would tell them there is nothing to stop paying.
  const paysTwice = paysForProBesideBusiness(personal);
  if (personal?.included_with && !paysTwice) return plain(isBusiness ? 'Pro included' : 'Included with Business');

  const freeUntil = personal?.free_until
    ?? (isFuture(subscription?.grandfathered_until, now) ? subscription?.grandfathered_until : null);
  const free = paysTwice ? null : formatBillingDate(freeUntil, 'short');
  if (free) return plain(`Free until ${free}`);

  if (isPro) {
    const renews = formatBillingDate(subscription?.expires_at ?? subscription?.renews_at, 'short');
    return plain(renews ? `Renews ${renews}` : null);
  }
  return plain('Upgrade to unlock');
};
